import { Button, Select, TextInput, addToast } from '@octano/global-ui';
import { useCallback, useEffect, useMemo } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { Col, Row } from 'reactstrap';

import { useStepState } from '../../../../components/step/useStepState';
import { SectionTitle } from '../../../../components/text';
import { PathsLayouts } from '../../../../config/routes';
import { useValidations } from '../../../../hooks/useValidations';
import { TranslationsKeys } from '../../../../locales/translations';
import {
  createAcademicOffer,
  updateAcademicOffer,
  updateAcademicOfferCurrentStep,
} from '../api';
import { useAcademicOfferWizard } from '../context/AcademicOfferWizardContext';
import { AcademicOfferInitialValues } from '../loaders/AcademicOfferDataLoader';
import { useAcademicOfferFormData } from '../loaders/AcademicOfferFormDataLoader';
import {
  AcademicOfferFinantialConfig,
  AcademicOfferRequirement,
  CeAcademicOfferStatus,
  CeAcademicOfferStepEnum,
  ContinuousEducationType,
  CreateCeAcademicOfferPayload,
  StudyPlan,
} from '../types';
import { AcademicOfferRequirements } from './AcademicOfferRequirements';

export interface AcademicOfferFormValues {
  programType: { label: string; value: ContinuousEducationType } | null;
  studyPlan: {
    label: string;
    value: number;
    data?: StudyPlan;
  } | null;
  campus: { label: string; value: number } | null;
  quota: string;
  requirements: AcademicOfferRequirement[];
  finantials: AcademicOfferFinantialConfig[];
}

interface Props {
  initialValues?: AcademicOfferInitialValues;
}

export function AcademicOfferCreateForm({ initialValues }: Props) {
  const { t } = useTranslation(
    TranslationsKeys.CONTINUOUS_EDUCATION_ACADEMIC_OFFERS,
  );
  const history = useHistory();
  const location = useLocation();
  const { validateTextNotEmpty } = useValidations();
  const { data: formData } = useAcademicOfferFormData();
  const {
    ceAcademicOfferId,
    setCeAcademicOfferId,
    offerStatus,
  } = useAcademicOfferWizard();
  const { nextStep } = useStepState();

  const isActive = offerStatus === CeAcademicOfferStatus.Activa;
  const isClosed = offerStatus === CeAcademicOfferStatus.Cerrada;
  const isReadOnly = isActive || isClosed;

  const searchParams = new URLSearchParams(location.search);
  const urlProgramType = searchParams.get(
    'programType',
  ) as ContinuousEducationType | null;

  const methods = useForm<AcademicOfferFormValues>({
    mode: 'all',
    defaultValues: initialValues,
  });

  const {
    control,
    handleSubmit,
    setValue,
    setError,
    formState: { isSubmitting },
  } = methods;

  const programType = useWatch({ control, name: 'programType' });
  const selectedStudyPlan = useWatch({ control, name: 'studyPlan' });

  // Opciones del selector de tipo de programa
  const programTypeOptions = useMemo(
    () => [
      {
        label: t('form.programType.course'),
        value: ContinuousEducationType.COURSE,
      },
      {
        label: t('form.programType.program'),
        value: ContinuousEducationType.PROGRAM_COURSE,
      },
    ],
    [t],
  );

  const defaultProgramType = useMemo(() => {
    if (!urlProgramType) {
      return null;
    }
    return programTypeOptions.find((o) => o.value === urlProgramType) ?? null;
  }, [urlProgramType, programTypeOptions]);

  useEffect(() => {
    if (defaultProgramType) {
      setValue('programType', defaultProgramType);
    }
  }, [defaultProgramType, setValue]);

  // Filtrar StudyPlans por tipo de programa seleccionado
  const studyPlanOptions = useMemo(() => {
    if (!formData?.studyPlans || !programType?.value) {
      return [];
    }

    return formData.studyPlans
      .filter((sp) => sp.studyPlanCe?.type === programType.value)
      .map((sp) => ({
        label: `${sp.code} - ${sp.name}`,
        value: sp.id,
        data: sp,
      }));
  }, [formData, programType]);

  // Opciones de campus
  const campusOptions = useMemo(() => {
    return (
      formData?.campuses.map((c) => ({ label: c.name, value: c.id })) ?? []
    );
  }, [formData]);

  // Datos del plan seleccionado
  const selectedPlanData = useMemo(() => {
    return selectedStudyPlan?.data ?? null;
  }, [selectedStudyPlan]);

  // Limpiar campos cuando cambia el tipo de programa (solo si no hay initialValues)
  useEffect(() => {
    // No limpiar si estamos en modo edición (hay initialValues)
    if (!ceAcademicOfferId) {
      setValue('studyPlan', null);
      setValue('campus', null);
    }
  }, [programType, setValue, ceAcademicOfferId]);

  const onSubmit = useCallback(
    async (values: AcademicOfferFormValues) => {
      if (!values.programType || !values.studyPlan || !values.campus) return;

      const finantials = values.finantials || [];

      for (let i = 0; i < finantials.length; i++) {
        if (finantials[i].value <= 0) {
          setError(`finantials.${i}.value`, {
            message: t('requirements.financial.valueGTZeroError'),
          });
          return;
        }
      }

      const payload: CreateCeAcademicOfferPayload = {
        studyPlanId: values.studyPlan.value,
        campusId: values.campus.value,
        quota: values.quota ? parseInt(values.quota, 10) : undefined,
        requirements: values.requirements,
        finantials: finantials,
      };

      // Determinar si crear o actualizar
      const isUpdating = !!ceAcademicOfferId;
      const response = ceAcademicOfferId
        ? await updateAcademicOffer(ceAcademicOfferId, payload)
        : await createAcademicOffer(payload);

      if (!response.error && response.data) {
        const offerId = response.data.id;

        // Solo establecer el ID en el contexto si es una nueva oferta
        if (!isUpdating) {
          setCeAcademicOfferId(offerId);
        }

        await updateAcademicOfferCurrentStep(
          offerId,
          CeAcademicOfferStepEnum.Sections,
        );

        addToast({
          icon: 'success',
          color: 'success',
          text: isUpdating
            ? t('messages.updateSuccess')
            : t('messages.createSuccess'),
        });

        // Si es una nueva oferta, redirigir (el wizard cargará el paso correcto automáticamente)
        // Si es actualización, avanzar al siguiente paso localmente
        if (!isUpdating) {
          history.replace(
            `${PathsLayouts.continuousEducation}/academic-offers/${offerId}/wizard`,
          );
        } else {
          nextStep();
        }
      } else {
        addToast({
          icon: 'error',
          color: 'danger',
          text: isUpdating
            ? t('messages.updateError')
            : t('messages.createError'),
        });
      }
    },
    [history, t, ceAcademicOfferId, setCeAcademicOfferId, nextStep, setError],
  );

  return (
    <>
      <SectionTitle text={t('create.title')} />
      <p>{t('create.subtitle')}</p>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Row className="mb-4">
          {/* Tipo de programa */}
          <Col xs={12} md={6} lg={3} className="mb-3">
            <Select
              name="programType"
              label={t('form.programType.label')}
              options={programTypeOptions}
              control={control}
              rules={{ validate: validateTextNotEmpty }}
              disabled={isReadOnly || !!urlProgramType}
            />
          </Col>

          {/* Selector de StudyPlan */}
          <Col xs={12} md={6} lg={3} className="mb-3">
            <Select
              name="studyPlan"
              label={t('form.studyPlan.label')}
              options={studyPlanOptions}
              control={control}
              rules={{ validate: validateTextNotEmpty }}
              disabled={isReadOnly || !programType}
            />
          </Col>

          {/* Selector de Campus */}
          <Col xs={12} md={6} lg={3} className="mb-3">
            <Select
              name="campus"
              label={t('form.campus.label')}
              options={campusOptions}
              control={control}
              rules={{ validate: validateTextNotEmpty }}
              disabled={isReadOnly}
            />
          </Col>

          {/* Código */}
          <Col xs={12} md={6} lg={3} className="mb-3">
            <TextInput
              name="code"
              label={t('form.code.label')}
              value={selectedPlanData?.code ?? '-'}
              disabled
            />
          </Col>

          {/* Modalidad */}
          <Col xs={12} md={6} lg={3} className="mb-3">
            <TextInput
              name="modality"
              label={t('form.modality.label')}
              value={selectedPlanData?.modality?.name ?? '-'}
              disabled
            />
          </Col>

          {/* Facultad */}
          <Col xs={12} md={6} lg={3} className="mb-3">
            <TextInput
              name="school"
              label={t('form.school.label')}
              value={selectedPlanData?.school?.name ?? '-'}
              disabled
            />
          </Col>

          {/* Cupos */}
          <Col xs={12} md={6} lg={3} className="mb-3">
            <TextInput
              name="quota"
              label={t('form.quota.label')}
              control={control}
              formatter={(value) => value.replace(/\D/g, '')}
              disabled={isReadOnly}
            />
          </Col>
        </Row>

        <FormProvider {...methods}>
          <AcademicOfferRequirements disabled={isReadOnly} />
        </FormProvider>

        <Row>
          <Col xs={12} md={6} lg={4} className="offset-md-6 offset-lg-8">
            <Row>
              <Col>
                <Button
                  text={t('form.cancel')}
                  outlined
                  fullwidth
                  onClick={() =>
                    history.push(
                      `${PathsLayouts.continuousEducation}/academic-offers`,
                    )
                  }
                />
              </Col>
              <Col>
                {isReadOnly ? (
                  <Button
                    text={t('wizard.next')}
                    fullwidth
                    onClick={() => nextStep()}
                  />
                ) : (
                  <Button
                    text={t('wizard.next')}
                    type="submit"
                    loading={isSubmitting}
                    fullwidth
                  />
                )}
              </Col>
            </Row>
          </Col>
        </Row>
      </form>
    </>
  );
}
